import { gql } from '@apollo/client';
import {
  BlogPlacementEnum,
  ListingGroupPurposeEnum,
  LocationEnum,
} from '@/__generated__/graphql';

export const SearchLocationQuery = gql`
  query Locations($locationName: String!) {
    locationAutocomplete(name: $locationName) {
      collection {
        name
        id
        type
      }
    }
  }
`;

export const GetLocationQuery = gql`
  query Location($locationSlug: String!) {
    location(slug: $locationSlug) {
      countryCode
      id
      name
      slug
      type
    }
  }
`;

export const SearchListingsMapQuery = gql`
  query Listings($filters: ListingFilter!, $locale: LocaleEnum) {
    availableListings(filters: $filters, page: 1, limit: 1000) {
      collection {
        numReviews
        ratingScore

        occupancy {
          numBedrooms
          numBeds
          numGuests
          petsAllowed
        }

        location {
          latitude
          longitude
        }

        previewImages {
          altText(locale: $locale)
          caption(locale: $locale)
          url
        }

        price {
          regular {
            price
            totalPrice
          }
          discount {
            price
            totalPrice
            percentage
            type
          }
          cleaningFee

          currency
          unit

          startDate
          endDate
        }

        label {
          name
          color
        }
        slug
        name(locale: $locale)
      }
      metadata {
        totalCount
      }
    }
  }
`;

export const SearchListingsQuery = gql`
  query Listings(
    $filters: ListingFilter!
    $page: Int!
    $limitValue: Int!
    $locale: LocaleEnum
  ) {
    availableListings(filters: $filters, page: $page, limit: $limitValue) {
      collection {
        numReviews
        ratingScore
        occupancy {
          numBedrooms
          numBeds
          numGuests
          petsAllowed
        }

        previewImages {
          altText(locale: $locale)
          caption(locale: $locale)
          url
        }

        price {
          regular {
            price
            totalPrice
          }
          discount {
            price
            totalPrice
            percentage
            type
          }
          cleaningFee

          currency
          unit

          startDate
          endDate
        }

        label {
          name
          color
        }
        slug
        name(locale: $locale)
      }

      metadata {
        totalCount
        totalPages
        currentPage
      }
    }
  }
`;

export const ListingsFilterValuesQuery = gql`
  query ListingsFilterValues($locale: LocaleEnum, $filters: ListingFilter) {
    listingsFilterValues(locale: $locale, filters: $filters) {
      amenities {
        iconName
        id
        name(locale: $locale)
      }
      price {
        min
        max
      }
    }
  }
`;

export const SearchListingDetailsQuery = gql`
  query ListingDetails($slug: String!, $locale: LocaleEnum) {
    showListing(slug: $slug) {
      calendar {
        date
        availableForCheckin
        minimumNightStay
      }

      featuredAmenities {
        iconName
        name(locale: $locale)
      }

      occupancy {
        numBedrooms
        numBeds
        numGuests
        petsAllowed
      }

      location {
        latitude
        longitude
      }

      regularAmenities {
        iconName
        name(locale: $locale)
      }

      previewImages {
        altText(locale: $locale)
        caption(locale: $locale)
        url
      }

      imageCategories {
        name(locale: $locale)
        images {
          altText(locale: $locale)
          caption(locale: $locale)
          url
        }
      }

      slug
      name(locale: $locale)
      description(locale: $locale)
      headline(locale: $locale)
      ratingScore
      numReviews
      label {
        name
        color
      }
      houseRules(locale: $locale)
      cancellationPolicy(locale: $locale)
      reviews(page: 1, limit: 6) {
        collection {
          author {
            name
            avatar
          }
          createdAt
          text(locale: $locale)
        }
        metadata {
          totalCount
          totalPages
        }
      }

      breadcrumbListingGroups {
        slug
        name(locale: $locale)
      }
    }
  }
`;

export const SearchListingDetailsPriceQuery = gql`
  query ListingDetailsPrice(
    $slug: String!
    $priceStartDate: ISO8601Date!
    $priceEndDate: ISO8601Date!
  ) {
    showListingPrice: showListing(slug: $slug) {
      slug
      price(startDate: $priceStartDate, endDate: $priceEndDate) {
        regular {
          price
          totalPrice
        }
        discount {
          price
          totalPrice
          percentage
          type
        }
        cleaningFee

        currency
        unit

        startDate
        endDate

        paymentSchedule {
          cleaningFee
          discountedPrice
          from
          paymentDate
          regularPrice
          to
        }
      }
    }
  }
`;

export const SearchListingDetailReviewsQuery = gql`
  query ListingDetails(
    $slug: String!
    $page: Int!
    $limit: Int!
    $locale: LocaleEnum
  ) {
    showListing(slug: $slug) {
      slug
      ratingScore
      numReviews
      reviews(page: $page, limit: $limit) {
        collection {
          author {
            name
            avatar
          }
          createdAt
          text(locale: $locale)
        }
        metadata {
          totalCount
          totalPages
        }
      }
    }
  }
`;

export const DESTINATIONS_PLACEMENT =
  ListingGroupPurposeEnum.ExploreDestinations;
export const SearchLocationGroupsQuery = gql`
  query searchLocations($filter: ListingGroupFilter, $locale: LocaleEnum!) {
    listingGroups(filter: $filter, page: 1, limit: 20) {
      collection {
        slug
        name(locale: $locale)
        menuTitle(locale: $locale)
        purposes
        location {
          id
          type
        }
      }
    }
  }
`;

export const LOCATION_ENUM = LocationEnum;
export const LONG_TERM_STAY_PLACEMENT = 'long_term_stay';

export const TRAVELLING_TIPS_SLUG = BlogPlacementEnum.TravellingTips;
export const HOME_OWNERS_SLUG = BlogPlacementEnum.HomeOwners;
export const SearchBlogs = gql`
  query Blogs($filter: BlogFilter!, $page: Int = 1, $limit: Int = 20) {
    blogs(filter: $filter, page: 1, limit: 20) {
      collection {
        id
        slug
        name
        posts(page: $page, limit: $limit) {
          collection {
            id
            coverImage {
              url
            }
            slug
            title
            createdAt
            author
          }
          metadata {
            currentPage
            totalPages
            totalCount
          }
        }
      }
    }
  }
`;

export const ShowBlogs = gql`
  query Blogs($filter: BlogFilter!, $page: Int = 1, $limit: Int = 20) {
    blogs(filter: $filter, page: $page, limit: $limit) {
      collection {
        id
        slug
        name
        posts(page: 1, limit: 20) {
          collection {
            id
            slug
            title
          }
          metadata {
            totalCount
            totalPages
          }
        }
      }
      metadata {
        currentPage
        totalPages
        totalCount
      }
    }
  }
`;

export const LATEST_NEWS_SLUG = BlogPlacementEnum.LatestNews;
export const SearchNews = gql`
  query SearchNews($filter: BlogFilter!, $page: Int = 1, $limit: Int = 20) {
    blogs(filter: $filter, page: 1, limit: 20) {
      collection {
        id
        slug
        name
        posts(page: $page, limit: $limit) {
          collection {
            id
            coverImage {
              url
            }
            slug
            title
            headline
            createdAt
            author
          }
          metadata {
            currentPage
            totalPages
          }
        }
      }
    }
  }
`;

export const ShowBlog = gql`
  query Blog(
    $slug: String
    $placement: BlogPlacementEnum
    $locale: LocaleEnum!
    $page: Int = 1
    $limit: Int = 20
  ) {
    showBlog(slug: $slug, placement: $placement, locale: $locale) {
      id
      slug
      name
      posts(page: $page, limit: $limit) {
        collection {
          id
          coverImage {
            url
          }
          slug
          title
          content
          createdAt
          author
        }
        metadata {
          currentPage
          totalPages
          totalCount
        }
      }
    }
  }
`;

export const GetBlogName = gql`
  query Blog(
    $slug: String
    $placement: BlogPlacementEnum
    $locale: LocaleEnum!
  ) {
    showBlog(slug: $slug, placement: $placement, locale: $locale) {
      name
    }
  }
`;

export const ShowPinned = gql`
  query ShowPinned($locale: LocaleEnum!) {
    pinnedPosts(locale: $locale) {
      content
      coverImage {
        url
      }
      createdAt
      headline
      id
      slug
      title
    }
  }
`;

export const ShowPost = gql`
  query ShowPost($slug: String!) {
    showPost(slug: $slug) {
      coverImage {
        url
      }
      createdAt
      id
      slug
      headline
      title
      content
      author
      canonicalPathOverride
    }
  }
`;

export const ShowReservation = gql`
  query ShowReservation($checkoutId: String!, $locale: LocaleEnum) {
    showReservation(checkoutId: $checkoutId) {
      cancellationPolicy(locale: $locale)
      checkIn
      checkOut
      confirmationCode
      contactDetails
      firstName
      houseRules(locale: $locale)
      lastName
      numGuests
      totalPrice
      listing {
        slug
        name(locale: $locale)
      }
    }
  }
`;

export const reservationCreate = gql`
  mutation ReservationCreate($input: ReservationCreateInput!) {
    reservationCreate(input: $input) {
      checkoutUrl
    }
  }
`;

export const SitemapQuery = gql`
  query Sitemap {
    sitemap {
      listingGroups {
        slug
        updatedAt
      }
      blogs {
        posts {
          slug
          locale
          updatedAt
        }
        slug
        updatedAt
      }
      listings {
        slug
        updatedAt
      }
    }
  }
`;

export const MenuItemsQuery = gql`
  query MenuItems($locale: LocaleEnum!) {
    menuItems(locale: $locale) {
      title
      blogSlug
      postSlug
      headerMenuPlacement
      footerMenuPlacement
    }
  }
`;

export const AddressSuggestQuery = gql`
  query addressSuggest($name: String!) {
    addressAutocomplete(name: $name)
  }
`;

export const EstimateProfitMutation = gql`
  mutation EstimateProfit($input: OnboardingPriceInquiryInput!) {
    ownerOnboardingPriceInquiry(input: $input) {
      success
      estimate {
        home {
          numBathrooms
          numBedrooms
          numGuests
          latitude
          longitude
        }
        revenue {
          nightlyAverage
          yearly
          occupancy
        }
        formattedAddress
      }
    }
  }
`;

export const CreateLeadMutation = gql`
  mutation CreateLead($input: OnboardingLeadCreateInput!) {
    ownerOnboardingLeadCreate(input: $input) {
      clientMutationId
      success
    }
  }
`;

export const LocationListingGroupsQuery = gql`
  query LocationGroups(
    $filter: ListingGroupFilter
    $locale: LocaleEnum!
    $page: Int!
    $limitValue: Int = 20
  ) {
    listingGroups(filter: $filter, page: $page, limit: $limitValue) {
      collection {
        slug
        name(locale: $locale)
        parentGroup {
          slug
          name(locale: $locale)
        }
        nestedGroups {
          slug
          name(locale: $locale)
          parentGroup {
            slug
            name(locale: $locale)
          }
          nestedGroups {
            slug
            name(locale: $locale)
            parentGroup {
              slug
              name(locale: $locale)
            }
            nestedGroups {
              slug
              name(locale: $locale)
              parentGroup {
                slug
                name(locale: $locale)
              }
            }
          }
        }
      }

      metadata {
        totalCount
        totalPages
      }
    }
  }
`;

export const ListingGroupQuery = gql`
  query ListingGroup($slug: String!, $locale: LocaleEnum!) {
    showListingGroup(slug: $slug) {
      slug
      name(locale: $locale)
      headline(locale: $locale)
      description(locale: $locale)
      purposes
      coverImage {
        url
      }
      location {
        id
        type
        name
      }
      blog(locale: $locale) {
        id
        slug
      }
      nestedGroups {
        slug
        name
        parentGroup {
          slug
          name
        }
        nestedGroups {
          slug
          name
          parentGroup {
            slug
            name
          }
        }
        nestedGroups {
          slug
          name
          parentGroup {
            slug
            name
          }
        }
      }
      parentGroup {
        slug
        name
        parentGroup {
          slug
          name
          parentGroup {
            slug
            name
          }
        }
      }
    }
  }
`;

export const LocationListingsQuery = gql`
  query LocationListings(
    $filters: ListingFilter!
    $page: Int!
    $limitValue: Int = 10
    $locale: LocaleEnum!
  ) {
    availableListings(filters: $filters, page: $page, limit: $limitValue) {
      collection {
        slug
        name(locale: $locale)
        numReviews
        ratingScore

        label {
          name
          color
        }

        occupancy {
          numBeds
          numGuests
          petsAllowed
        }

        previewImages {
          altText(locale: $locale)
          caption(locale: $locale)
          url
        }

        price {
          regular {
            price
            totalPrice
          }
          discount {
            price
            totalPrice
            percentage
            type
          }
          cleaningFee

          currency
          unit

          startDate
          endDate
        }
      }

      metadata {
        totalCount
        totalPages
      }
    }
  }
`;

export const SitemapListingsQuery = gql`
  query SitemapListings(
    $page: Int!
    $limitValue: Int = 20
    $locale: LocaleEnum
  ) {
    availableListings(
      filters: {
        locationId: "SE"
        occupancy: { numGuests: 1, withPets: false }
      }
      page: $page
      limit: $limitValue
    ) {
      collection {
        slug
        name(locale: $locale)
      }

      metadata {
        totalCount
        totalPages
      }
    }
  }
`;

export const SearchReviews = gql`
  query SearchReviews($locale: LocaleEnum, $page: Int = 1, $limit: Int = 20) {
    guestReviews(page: $page, limit: $limit) {
      collection {
        id
        rating
        createdAt
        author {
          avatar
          name
        }
        listing {
          slug
          name(locale: $locale)
          previewImages {
            altText(locale: $locale)
            caption(locale: $locale)
            url
          }
        }
        text(locale: $locale)
        textOriginal
      }
      metadata {
        currentPage
        totalPages
        totalCount
      }
    }
  }
`;
